import { useCallback, useEffect, useRef, useState } from "react";
import { X, Download, ZoomIn, Volume2, VolumeX } from "lucide-react";
import ImageLightbox from "./ImageLightbox";
import BookView from "./catalog/BookView";
import ScrollView from "./catalog/ScrollView";

function useMode() {
  const calc = () => {
    const w = window.innerWidth, h = window.innerHeight;
    if (w >= 900 && h >= 560) return "book";
    return w > h ? "landscape" : "portrait";
  };
  const [m, setM] = useState(calc);
  useEffect(() => {
    const r = () => setM(calc());
    window.addEventListener("resize", r);
    window.addEventListener("orientationchange", r);
    return () => { window.removeEventListener("resize", r); window.removeEventListener("orientationchange", r); };
  }, []);
  return m;
}

export default function CatalogViewer({ catalog, lang, onClose }) {
  const mode = useMode();
  const [zoom, setZoom] = useState(null);
  const zoomRef = useRef(null);
  zoomRef.current = zoom;
  const page = useRef(0);
  const onPage = useCallback((k) => { page.current = k; }, []);
  const [sound, setSound] = useState(() => localStorage.getItem("hgw-page-sound") !== "off");
  const toggleSound = () => { const v = !sound; setSound(v); localStorage.setItem("hgw-page-sound", v ? "on" : "off"); };
  const pages = catalog.hd || catalog.pages;

  useEffect(() => {
    const k = (e) => { if (e.key === "Escape" && zoomRef.current === null) onClose(); };
    window.addEventListener("keydown", k);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", k); document.body.style.overflow = ""; };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] bg-[radial-gradient(ellipse_at_center,#25262b_0%,#0b0b0d_75%)] flex flex-col" data-testid="catalog-viewer" data-mode={mode}>
      <div className="flex items-center justify-between gap-4 px-4 md:px-8 h-14 text-white/80 text-[13px] shrink-0">
        <span className="truncate">{catalog.title[lang]}</span>
        <div className="flex items-center gap-1">
          <button onClick={() => setZoom(page.current)} aria-label="Zoom" className="w-10 h-10 rounded-full hover:bg-white/10 inline-flex items-center justify-center" data-testid="viewer-zoom"><ZoomIn className="w-[18px] h-[18px]" /></button>
          {mode === "book" && (
            <button onClick={toggleSound} aria-label="Audio" className="w-10 h-10 rounded-full hover:bg-white/10 inline-flex items-center justify-center" data-testid="viewer-sound-toggle">{sound ? <Volume2 className="w-[18px] h-[18px]" /> : <VolumeX className="w-[18px] h-[18px]" />}</button>
          )}
          <a href={catalog.pdf} download className="w-10 h-10 rounded-full hover:bg-white/10 inline-flex items-center justify-center" aria-label="PDF" data-testid="viewer-download"><Download className="w-[18px] h-[18px]" /></a>
          <button onClick={onClose} aria-label="Chiudi" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center ml-1" data-testid="viewer-close"><X className="w-5 h-5" /></button>
        </div>
      </div>
      {mode === "book"
        ? <BookView catalog={catalog} lang={lang} sound={sound} zoomOpen={zoom !== null} start={page.current} onZoom={setZoom} onPage={onPage} />
        : <ScrollView key={mode} catalog={catalog} lang={lang} landscape={mode === "landscape"} start={page.current} onZoom={setZoom} onPage={onPage} />}
      <ImageLightbox images={pages} startIndex={zoom ?? 0} open={zoom !== null} onClose={() => setZoom(null)} />
    </div>
  );
}
